import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileText, TrendingUp, TrendingDown, Activity, AlertCircle } from 'lucide-react';
import { reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

interface ProfitLossSummary {
  total_sales: number;
  total_cogs: number;
  total_refunds: number;
  total_expenses: number;
  total_paid_commission?: number;
  net_sales: number;
  gross_profit: number;
  net_profit: number;
}

interface ProfitLossDaily {
  date: string;
  sales: number;
  cogs: number;
  refunds: number;
  expenses: number;
  paid_commission?: number;
  net_sales: number;
  gross_profit: number;
  net_profit: number;
}

interface ProfitLossResponse {
  summary: ProfitLossSummary;
  daily_breakdown: ProfitLossDaily[];
}

export default function ProfitLossReportSection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const { data, isLoading, error } = useQuery({
    queryKey: ['profitLossReport', from_date, to_date],
    queryFn: async () => {
      const res = await reportsApi.getProfitLossReport({ from_date, to_date });
      return res as ProfitLossResponse;
    },
  });

  const handleExport = () => {
    if (!data?.daily_breakdown?.length) return;
    
    const rows = data.daily_breakdown.map((row) => ({
      date: row.date,
      sales: row.sales.toFixed(2),
      refunds: row.refunds.toFixed(2),
      net_sales: row.net_sales.toFixed(2),
      cogs: row.cogs.toFixed(2),
      gross_profit: row.gross_profit.toFixed(2),
      expenses: row.expenses.toFixed(2),
      paid_commission: (row.paid_commission || 0).toFixed(2),
      net_profit: row.net_profit.toFixed(2),
    }));

    const headers = [
      { key: 'date', label: t('nav.date', 'Date') },
      { key: 'sales', label: t('reports.totalSales', 'Total Sales') },
      { key: 'refunds', label: t('reports.refunds', 'Refunds') },
      { key: 'net_sales', label: t('reports.netSales', 'Net Sales') },
      { key: 'cogs', label: t('reports.cogs', 'COGS') },
      { key: 'gross_profit', label: t('reports.grossProfit', 'Gross Profit') },
      { key: 'expenses', label: t('reports.expenses', 'Expenses') },
      { key: 'paid_commission', label: t('reports.paidCommission', 'Paid Commission') },
      { key: 'net_profit', label: t('reports.netProfit', 'Net Profit') },
    ];

    downloadCsv(`Profit_Loss_Report_${from_date}_to_${to_date}.csv`, headers, rows);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p>Failed to load profit and loss report.</p>
      </div>
    );
  }

  const summary = data?.summary;
  const isProfitable = summary ? summary.net_profit >= 0 : true;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col lg:flex-row gap-4 justify-between lg:items-end">
        <ReportDateRangeFilter
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          customFrom={customFrom}
          onCustomFromChange={setCustomFrom}
          customTo={customTo}
          onCustomToChange={setCustomTo}
        />
        <ReportExportButton onClick={handleExport} disabled={isLoading || !data?.daily_breakdown?.length} />
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 h-28" />
            ))}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 h-96" />
        </div>
      ) : summary ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Sales Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-4 -mt-4 opacity-50 pointer-events-none" />
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Net Sales</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(summary.net_sales)}</p>
                </div>
                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg shrink-0">
                  <Activity className="w-5 h-5" />
                </div>
              </div>
              <div className="text-sm text-gray-500">
                Gross: {formatMoney(summary.total_sales)} <br/>
                Refunds: {formatMoney(summary.total_refunds)}
              </div>
            </div>

            {/* COGS Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-orange-50 rounded-bl-full -mr-4 -mt-4 opacity-50 pointer-events-none" />
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Cost of Goods (COGS)</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(summary.total_cogs)}</p>
                </div>
                <div className="p-2.5 bg-orange-50 text-orange-600 rounded-lg shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
              </div>
              <p className="text-sm text-gray-500">Total cost of items sold</p>
            </div>

            {/* Expenses Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-rose-50 rounded-bl-full -mr-4 -mt-4 opacity-50 pointer-events-none" />
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Expenses</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(summary.total_expenses)}</p>
                </div>
                <div className="p-2.5 bg-rose-50 text-rose-600 rounded-lg shrink-0">
                  <TrendingDown className="w-5 h-5" />
                </div>
              </div>
              <p className="text-sm text-gray-500">
                {t('reports.paidCommission', 'Paid Commission')}: {formatMoney(summary.total_paid_commission || 0)}
              </p>
            </div>

            {/* Net Profit Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 relative overflow-hidden">
              <div className={`absolute top-0 right-0 w-24 h-24 rounded-bl-full -mr-4 -mt-4 opacity-50 pointer-events-none ${isProfitable ? 'bg-emerald-50' : 'bg-red-50'}`} />
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Net Profit</p>
                  <p className={`text-2xl font-bold mt-1 ${isProfitable ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatMoney(summary.net_profit)}
                  </p>
                </div>
                <div className={`p-2.5 rounded-lg shrink-0 ${isProfitable ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                  {isProfitable ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                </div>
              </div>
              <div className="text-sm text-gray-500">
                Gross Profit: {formatMoney(summary.gross_profit)}
              </div>
            </div>
          </div>

          {/* Daily Breakdown Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col max-h-[600px]">
            <div className="p-4 border-b border-gray-100 shrink-0 bg-white">
              <h3 className="font-semibold text-gray-900">Daily Breakdown</h3>
            </div>
            
            <div className="overflow-x-auto flex-1 min-h-0">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-gray-50 text-gray-500 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 font-medium bg-gray-50">Date</th>
                    <th className="px-4 py-3 font-medium text-right bg-gray-50">Total Sales</th>
                    <th className="px-4 py-3 font-medium text-right bg-gray-50">Refunds</th>
                    <th className="px-4 py-3 font-medium text-right bg-gray-50">Net Sales</th>
                    <th className="px-4 py-3 font-medium text-right bg-gray-50">COGS</th>
                    <th className="px-4 py-3 font-medium text-right bg-gray-50">Gross Profit</th>
                    <th className="px-4 py-3 font-medium text-right bg-gray-50">Expenses</th>
                    <th className="px-4 py-3 font-medium text-right bg-gray-50">Net Profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.daily_breakdown?.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        <div className="flex flex-col items-center justify-center">
                          <FileText className="w-8 h-8 text-gray-300 mb-2" />
                          <p>No activity found for this period</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    data?.daily_breakdown.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">{row.date}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{formatMoney(row.sales)}</td>
                        <td className="px-4 py-3 text-right text-red-600">{formatMoney(row.refunds)}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">{formatMoney(row.net_sales)}</td>
                        <td className="px-4 py-3 text-right text-orange-600">{formatMoney(row.cogs)}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">{formatMoney(row.gross_profit)}</td>
                        <td className="px-4 py-3 text-right text-rose-600">{formatMoney(row.expenses)}</td>
                        <td className={`px-4 py-3 text-right font-bold ${row.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatMoney(row.net_profit)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
